// Single source of truth for the Modern Exit Websites taxonomy + pricing.
//
// Every marketing page (`/websites`, `/packages`, `/pricing`) renders the same
// four tiers using this file. To change a name, price, or feature list, edit
// this file once — all three pages update together.
//
// NOTE: The Website Build Brief (`/build-brief`) and the Stripe Payment Link
// map are intentionally NOT sourced from here yet. Those re-align once the
// operator produces new Stripe Payment Links at the new prices so we don't
// ship a UI that leads customers to a mispriced checkout.

export const WEBSITE_PACKAGES = [
  {
    key: "starter",
    title: "Starter Website",
    price: "$1,500",
    priceLabel: "One-time",
    shortDescription:
      "A clean, conversion-ready one-page website for getting your business online fast.",
    builtFor: [
      "New service businesses",
      "Solo operators",
      "Coaches and consultants",
    ],
    includes: [
      "One-page website",
      "Mobile responsive layout",
      "Clear service positioning",
      "Lead capture form",
      "Basic call-to-action structure",
    ],
    seo: {
      title: "Starter SEO Setup",
      description:
        "Basic search engine setup for a clean website launch.",
      bullets: [
        "SEO page title",
        "Meta description",
        "One primary keyword focus",
        "Basic heading structure",
        "Image alt text",
        "Clean URL setup",
        "Mobile-friendly structure check",
        "Sitemap guidance",
        "Google indexing guidance",
      ],
    },
    cta: "Start Website Build",
    badge: null,
    emphasized: false,
  },
  {
    key: "growth",
    title: "Growth Website",
    price: "Starting at $2,500",
    priceLabel: "One-time",
    shortDescription:
      "A multi-page website built to improve trust, clarity, and lead conversion.",
    builtFor: [
      "Businesses with multiple services",
      "Brands that need more credibility",
      "Service providers ready to look more established",
    ],
    includes: [
      "Multi-page website structure",
      "Homepage",
      "Service pages",
      "Contact page",
      "Mobile optimization",
      "Lead capture forms",
      "Clear conversion sections",
    ],
    seo: {
      title: "Growth SEO Setup",
      description:
        "Page-level SEO structure for service businesses that need stronger local visibility.",
      bullets: [
        "SEO page titles for core pages",
        "Meta descriptions for core pages",
        "Keyword mapping by page",
        "Heading structure optimization",
        "Image alt text",
        "Clean URL structure",
        "Internal linking recommendations",
        "Sitemap setup guidance",
        "Google Search Console guidance",
        "Basic local SEO structure",
        "Service keyword placement",
      ],
    },
    cta: "Build My Website",
    badge: "Most Popular",
    emphasized: true,
  },
  {
    key: "premium",
    title: "Premium Website",
    price: "Starting at $3,500",
    priceLabel: "One-time",
    shortDescription:
      "A premium brand website with stronger visuals, sharper messaging, and a more polished buyer experience.",
    builtFor: [
      "Established service businesses",
      "Premium brands",
      "Operators ready to upgrade their online presence",
    ],
    includes: [
      "Premium homepage design",
      "Full service page structure",
      "Conversion copy sections",
      "Brand-aligned visuals",
      "Strong CTA flow",
      "Mobile-first optimization",
      "Trust and proof sections",
    ],
    seo: {
      title: "Premium SEO Setup",
      description:
        "Full website SEO foundation built around visibility, service intent, and lead generation.",
      bullets: [
        "Full website SEO structure",
        "SEO titles for all primary pages",
        "Meta descriptions for all primary pages",
        "Keyword strategy by service",
        "Local SEO structure",
        "Service-area keyword placement",
        "Heading hierarchy optimization",
        "Image alt text",
        "Internal linking strategy",
        "Clean URL structure",
        "Sitemap setup",
        "Google Search Console guidance",
        "Indexing checklist",
        "Conversion-focused page structure",
        "SEO recommendations for future content",
      ],
    },
    cta: "Apply for Premium Build",
    badge: null,
    emphasized: false,
  },
];

export const LANDING_PAGE_PACKAGE = {
  key: "landing-page",
  title: "Landing Page Build",
  price: "$997",
  priceLabel: "One-time · Add-on / campaign page",
  shortDescription:
    "A focused page built for one offer, campaign, workshop, event, or lead magnet.",
  builtFor: [
    "Paid ads",
    "Workshops",
    "Events",
    "Lead magnets",
    "Single-service campaigns",
  ],
  includes: [
    "One conversion-focused landing page",
    "Offer positioning",
    "Lead capture form",
    "CTA section",
    "Mobile optimization",
  ],
  cta: "Build Landing Page",
  badge: null,
  emphasized: false,
};

/** Stable data-testid slug for each package (matches pre-existing slugs
 *  so test selectors + any inbound links keep working). */
export const slugForPackage = (pkg) =>
  pkg.key === "landing-page" ? "landing-page-build" : `${pkg.key}-website`;

/** Adapter for `/websites` — uses local `PackageCard` with the { name,
 *  price, badge, bestFor (string), features, cta, emphasized } shape. */
export const toWebsitesCardShape = (pkg) => ({
  id: slugForPackage(pkg),
  name: pkg.title,
  price: pkg.price,
  badge: pkg.badge || undefined,
  bestFor: pkg.shortDescription,
  features: pkg.includes,
  seo: pkg.seo,
  cta: pkg.cta,
  emphasized: pkg.emphasized,
});

/** Adapter for `/packages` — uses shared `PackageCard` from
 *  OfferingPrimitives with the { title, price, priceLabel, description,
 *  builtFor, includes, badge, emphasized } shape. */
export const toPackagesCardShape = (pkg) => ({
  id: slugForPackage(pkg),
  title: pkg.title,
  price: pkg.price,
  priceLabel: pkg.priceLabel,
  badge: pkg.badge || undefined,
  description: pkg.shortDescription,
  builtFor: pkg.builtFor,
  includes: pkg.includes,
  seo: pkg.seo,
  cta: pkg.cta,
  emphasized: pkg.emphasized,
});

/** Adapter for `/pricing` — identical shape to /packages but ids get a
 *  `pricing-` prefix so Playwright selectors don't collide when both pages
 *  are tested in the same session. */
export const toPricingCardShape = (pkg) => ({
  ...toPackagesCardShape(pkg),
  id: `pricing-${slugForPackage(pkg)}`,
});
